import { DateTime as DT } from 'luxon';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { SupplierRequestComingType } from '@econtractor/types';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import { withPrimaryCurrency } from 'utils/helpers/currency/currencyHelpers';
import { useSettings } from 'context/SettingsContext';
import CountBadge from '../../CounBadge';
import PaymentCloudFiles from '../../OffPayments/tabs/PaymentCloudFiles';

interface ComingBatchesProps {
    comings: SupplierRequestComingType[];
}

const ComingBatches = ({ comings }: ComingBatchesProps) => {
    const { t } = useTranslation(['Warehouses']);
    const { localSettings } = useSettings();
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;

    if (!comings?.length) return null;

    return (
        <div className="mb-3">
            <h6 className="text-muted mb-2">{t('supplierRequests.view.comingsTitle')}</h6>
            {comings.map((coming, index) => {
                const dt = DT.fromMillis(Number(coming.dt || 0));
                // comings ordered newest-first — №1 is the earliest coming of this request
                const batchNo = comings.length - index;
                return (
                    <div key={coming.ID} className="border rounded mb-2 p-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-medium">
                                {t('supplierRequests.view.comingBatch', { id: batchNo })}
                            </span>
                            <span className="text-muted f-12">
                                {dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '—'}
                            </span>
                        </div>
                        <div className="crm-table-wrap">
                            <Table size="sm" className="mb-0 align-middle">
                                <thead>
                                    <tr className="bg-light">
                                        <th>{t('supplierRequests.create.colName')}</th>
                                        <th className="text-end">
                                            {t('comingFields.price')}
                                        </th>
                                        <th className="text-end">
                                            {t('supplierRequests.create.colCount')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(coming.items || []).map((item) => (
                                        <tr key={item.ID}>
                                            <td>
                                                <div>{item.prodName}</div>
                                                {item.prodArticle ? (
                                                    <div className="text-muted f-12">
                                                        {item.prodArticle}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="text-end">
                                                {withPrimaryCurrency(formatPrice(item.price), rate)}
                                            </td>
                                            <td className="text-end">
                                                <CountBadge count={item.itemCount} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {coming.paymentAttachCloud?.length ? (
                            <div className="mt-2 px-3">
                                <PaymentCloudFiles
                                    paymentId={Number(coming.paymentId || 0)}
                                    files={coming.paymentAttachCloud}
                                />
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
};

export default ComingBatches;
